import { api } from '../services/axiosConfig';

/** Mirrors `src/data/handbook.ts`. The handbook is member-gated server-side
 * and never bundled, so the frontend only knows its shape, not its content. */
export interface Localized {
  de: string;
  en: string;
}

export type HandbookLang = keyof Localized;

export type HandbookScene =
  | 'chain'
  | 'spawn'
  | 'scan'
  | 'mass'
  | 'polarised'
  | 'threat'
  | 'sites'
  | 'route'
  | 'chaindepth'
  | 'holecontrol'
  | 'homedefence'
  | 'types'
  | 'mapping'
  | 'rolling'
  | 'rollnormal'
  | 'rollshrink'
  | 'rollrage'
  | 'emergency'
  | 'roles'
  | 'licence'
  | 'checklist'
  | 'closing';

export type HandbookBlock =
  | { kind: 'text'; body: Localized }
  | { kind: 'bullets'; items: Localized[] }
  | { kind: 'steps'; items: Localized[] }
  | { kind: 'table'; head: Localized[]; rows: Localized[][] }
  | { kind: 'callout'; title: Localized; body: Localized }
  /** No per-diagram scene: the chapter's own `scene` drives the margin canvas,
   * and that is the animation. `animate: 'lines'` reveals this ASCII line by
   * line — the same diagram, not a second copy of it. */
  | { kind: 'diagram'; caption: Localized | null; body: Localized; animate?: 'lines' }
  | { kind: 'checklist'; id: string; items: Localized[]; note?: Localized }
  | { kind: 'links'; items: { label: string; href: string }[] };

export interface HandbookSection {
  title: Localized | null;
  /** Overrides the chapter's scene from this section onward. */
  scene?: HandbookScene;
  blocks: HandbookBlock[];
}

export interface HandbookChapter {
  id: string;
  number: number | null;
  title: Localized;
  scene: HandbookScene;
  sections: HandbookSection[];
}

export interface Handbook {
  title: Localized;
  subtitle: Localized;
  tagline: Localized;
  document: string;
  version: string;
  validFrom: string;
  audience: Localized;
  usage: Localized;
  region: string;
  lead: { title: Localized; body: Localized };
  notice: Localized;
  masthead: { caption: Localized | null; body: Localized };
  chapters: HandbookChapter[];
}

export const fetchHandbook = async (): Promise<Handbook> => {
  const { data } = await api.get<Handbook>('/api/handbook');
  return data;
};
